/**
 * system 消息 metadata.type 分类的单一数据源。
 *
 * 三类 system 消息语义：
 * - UI 层（UI_LAYER_SYSTEM_TYPES）：只在气泡展示，不送进 LLM prompt。
 * - LLM 专用指令：需要送进 LLM，但也要在气泡展示。不列入本白名单。
 *   - call_system_prompt：通话接通时提示 AI 先开口
 *   - user_post_comment：用户评论 AI 朋友圈，需要 AI 感知并回复
 * - prompt 层：由三层 prompt 生成器写入，无 metadata.type，可被重建逻辑安全替换。
 *
 * chat.ts 和 apps/stage-web 的 data-migration.ts 必须共用此常量，防止白名单与
 * 迁移推断枚举漂移。
 */

export const UI_LAYER_SYSTEM_TYPES: ReadonlySet<string> = new Set([
  'offline_switch',
  'message_recalled',
  'relationship_change',
  'plugin_notification',
  'call_end',
  'call_system',
  'legacy_ui',
  'pat',
  // 拉黑系统的纯 UI 事件（仅气泡展示，不送进 LLM）
  // 关于 LLM 是否能看到这些事件：通过 useEnhancedPrompt 的 BLOCK_STATE_SECTION 结构化注入
  // 让 AI 知道当前 blockStatus，不需要靠系统消息的自然语言。系统消息存在易引起人称误读
  // （"你接受了..." LLM 视角下"你"=AI，会理解反），所以这里全部过滤。
  'block_by_user',
  'unblock_by_user',
  'force_unblock_by_user',
  'block_by_ai',
  'friend_request_sent',
  'friend_request_user_accept',
  'friend_request_user_reject',
  'friend_request_accepted',
  'friend_request_rejected',
  'friend_request_user_cancel',
])

export function getSystemMetadataType(meta: unknown): string | null {
  if (!meta || typeof meta !== 'object')
    return null
  const type = (meta as { type?: unknown }).type
  return typeof type === 'string' && type.length > 0 ? type : null
}

export function isPromptLayerSystemMessage(m: { role?: string, metadata?: unknown }): boolean {
  if (m.role !== 'system')
    return false
  return getSystemMetadataType(m.metadata) === null
}

/**
 * 是否是「仅 UI 展示」的系统消息（不送进 LLM prompt）
 */
export function isUiOnlySystemMessage(m: { role?: string, metadata?: unknown }): boolean {
  if (m.role !== 'system')
    return false
  const type = getSystemMetadataType(m.metadata)
  return type !== null && UI_LAYER_SYSTEM_TYPES.has(type)
}

/**
 * UI 可见的系统消息类型白名单。
 * 这些类型的 system 消息会在聊天气泡里显示给用户看。
 * 其他 system 消息默认不显示（如：prompt 注入、AI 决策上下文等）。
 *
 * 注意：此处白名单只决定「是否在 UI 显示」，与 UI_LAYER_SYSTEM_TYPES（决定「是否过滤掉不发给 LLM」）是两回事。
 */
export const UI_VISIBLE_SYSTEM_TYPES: ReadonlySet<string> = new Set([
  // 通用 UI 事件
  'call_end',
  'offline_switch',
  'plugin_notification',
  'message_recalled',
  'relationship_change',
  'pat',
  // 拉黑系统的 UI 事件（用户能看到的状态变更）
  'block_by_user',
  'unblock_by_user',
  'force_unblock_by_user',
  'block_by_ai',
  'friend_request_sent',
  'friend_request_user_accept',
  'friend_request_user_reject',
  'friend_request_accepted',
  'friend_request_rejected',
  'friend_request_user_cancel',
])

/**
 * 是否应该在 UI 中显示这条系统消息
 */
export function isUiVisibleSystemMessage(m: { role?: string, metadata?: unknown }): boolean {
  if (m.role !== 'system')
    return false
  const type = getSystemMetadataType(m.metadata)
  return type !== null && UI_VISIBLE_SYSTEM_TYPES.has(type)
}
